// Utilities for interacting with Google Identity Services (GIS)

let gisReadyPromise: Promise<void> | null = null;
let initializedClientId: string | null = null;

export const loadGoogleScript = (): Promise<void> => {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('Google Identity Services requires a browser environment'));
  }

  if (window.google?.accounts?.id) {
    return Promise.resolve();
  }

  if (!gisReadyPromise) {
    gisReadyPromise = new Promise<void>((resolve, reject) => {
      let settled = false;

      const succeed = () => {
        if (settled) return;
        settled = true;
        cleanup();
        resolve();
      };

      const fail = (error: Error) => {
        if (settled) return;
        settled = true;
        cleanup();
        reject(error);
      };

      const onReady = () => {
        if (window.google?.accounts?.id) {
          succeed();
        }
      };

      let poll: number | undefined = window.setInterval(onReady, 50);
      let timeout: number | undefined = window.setTimeout(
        () => fail(new Error('Timed out waiting for Google Identity Services to load')),
        10000
      );

      const cleanup = () => {
        window.removeEventListener('gsi_loaded', onReady);
        if (poll !== undefined) window.clearInterval(poll);
        if (timeout !== undefined) window.clearTimeout(timeout);
      };

      window.addEventListener('gsi_loaded', onReady, { once: true });

      // Immediately check in case the script loaded before we set listeners
      onReady();
    });
  }

  return gisReadyPromise;
};

export type GoogleCredentialCallback = (credential: string) => void;

export const initGoogleSignIn = (
  clientId: string,
  onCredential: GoogleCredentialCallback
) => {
  const accountsId = window.google?.accounts?.id;
  if (!accountsId) {
    throw new Error('Google Identity Services not loaded');
  }

  if (initializedClientId && initializedClientId !== clientId) {
    return;
  }

  if (!initializedClientId) {
    accountsId.initialize({
      client_id: clientId,
      callback: (response: { credential: string }) => {
        if (response?.credential) onCredential(response.credential);
      },
      auto_select: false,
      cancel_on_tap_outside: true,
      ux_mode: 'popup',
    });
    initializedClientId = clientId;
  }
};

export const renderGoogleButton = (container: HTMLElement) => {
  const accountsId = window.google?.accounts?.id;
  if (!accountsId) return;

  while (container.firstChild) {
    container.removeChild(container.firstChild);
  }

  accountsId.renderButton(container, {
    theme: 'outline',
    size: 'large',
    text: 'signin_with',
    shape: 'pill',
    width: 320,
  });
};

export const resetGoogleInit = () => {
  initializedClientId = null;
  // Also reset the ready promise to allow fresh initialization
  gisReadyPromise = null;
};

export const promptOneTap = () => {
  try {
    window.google?.accounts?.id?.prompt?.();
  } catch (err) {
    // silently ignore
  }
};

export const disableAutoSelect = () => {
  try {
    window.google?.accounts?.id?.disableAutoSelect?.();
  } catch (err) {
    // silently ignore
  }
};

// Minimal JWT decoder (no crypto verification; for demo/client use only)
export const decodeJwt = <T = Record<string, unknown>>(token: string): T => {
  const base64Url = token.split('.')[1];
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  const jsonPayload = decodeURIComponent(
    atob(base64)
      .split('')
      .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
      .join('')
  );
  return JSON.parse(jsonPayload) as T;
};
